import { useCallback, useEffect, useState } from "react";
import { userRequest } from "../api/requestMethods";
import {
  clearGroupPayment,
  groupPaymentFor,
  paymentPollOutcome,
  paymentStatusPath,
} from "../utils/groupBasketPayment";

const POLL_MS = 5000;
const WATCH_FOR_MS = 4 * 60 * 1000;

/*
 * Watches the payment this browser started for a basket (a share, the
 * remainder, or the host's one order) until it settles. The basket's own
 * share status is what the screen shows; this only asks the rail's status
 * read (section 6) every few seconds, which also makes the server check with
 * the provider, and refetches the basket the moment the rail has an answer.
 * A timeout is never a failure: it just stops asking.
 *
 * @returns {{ payment, track, forget }}
 *   payment  the record of the payment in flight, or null
 *   track    re-read the record (after a pay sheet stored one)
 *   forget   drop the record (the basket settled it)
 */
export default function useGroupPaymentWatch({ basketId, refresh, onRailAnswer }) {
  const [payment, setPayment] = useState(() => groupPaymentFor(basketId));

  const track = useCallback(() => setPayment(groupPaymentFor(basketId)), [basketId]);
  const forget = useCallback(() => {
    clearGroupPayment();
    setPayment(null);
  }, []);

  useEffect(() => {
    setPayment(groupPaymentFor(basketId));
  }, [basketId]);

  useEffect(() => {
    if (!payment?.orderId) return undefined;
    let cancelled = false;
    const ask = async () => {
      if (cancelled) return;
      if (Date.now() - Number(payment.startedAt) > WATCH_FOR_MS) {
        clearInterval(timer);
        return;
      }
      try {
        const { data } = await userRequest.get(paymentStatusPath(payment));
        const outcome = paymentPollOutcome(payment, data);
        if (cancelled || outcome === "pending") return;
        clearInterval(timer);
        if (onRailAnswer) onRailAnswer(payment, outcome, data);
        refresh();
      } catch (_) {
        /* the next tick asks again; the basket still shows the truth */
      }
    };
    const timer = setInterval(ask, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [payment, refresh, onRailAnswer]);

  return { payment, track, forget };
}
